import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ProviderShell from "@/components/ProviderShell";
import AdminShell from "@/components/AdminShell";

import Login from "@/pages/Login";
import ProviderHome from "@/pages/ProviderHome";
import DailyNoteForm from "@/pages/DailyNoteForm";
import EMARForm from "@/pages/EMARForm";import AppointmentForm from "@/pages/AppointmentForm";
import ProviderResources from "@/pages/ProviderResources";
import EvacuationDrillForm from "@/pages/EvacuationDrillForm";

import AdminOversight from "@/pages/AdminOversight";
import AdminNotes from "@/pages/AdminNotes";
import AdminClientCalendar from "@/pages/AdminClientCalendar";
import AdminBudgetTracking from "@/pages/AdminBudgetTracking";
import AdminAuditReport from "@/pages/AdminAuditReport";
import AdminFormBuilder from "@/pages/AdminFormBuilder";
import AdminResources from "@/pages/AdminResources";
import AdminProfiles from "@/pages/AdminProfiles";
import AdminClientProfile from "@/pages/AdminClientProfile";
import AdminContractorProfile from "@/pages/AdminContractorProfile";
import AdminAppointments from "@/pages/AdminAppointments";
import AdminNotifications from "@/pages/AdminNotifications";
import AdminAccounts from "@/pages/AdminAccounts";
import AdminLinks from "@/pages/AdminLinks";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminChangeRequests from "@/pages/AdminChangeRequests";
import AdminMedRequests from "@/pages/AdminMedRequests";
import ProviderClientProfile from "@/pages/ProviderClientProfile";
import ProviderHousehold from "@/pages/ProviderHousehold";

function Root() {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "admin" ? "/admin" : "/provider"} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Root />} />
          <Route path="/login" element={<Login />} />

          <Route
            path="/provider"
            element={
              <ProtectedRoute role="provider">
                <ProviderShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<ProviderHome />} />
            <Route path="daily-note" element={<DailyNoteForm />} />
            <Route path="emar" element={<EMARForm />} />
            <Route path="appointment" element={<AppointmentForm />} />
            <Route path="resources" element={<ProviderResources />} />
            <Route path="evacuation-drill" element={<EvacuationDrillForm />} />
            <Route path="client/:clientId" element={<ProviderClientProfile />} />
            <Route path="household" element={<ProviderHousehold />} />
          </Route>

          <Route
            path="/admin"
            element={
              <ProtectedRoute role="admin">
                <AdminShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminOversight />} />
            <Route path="notes" element={<AdminNotes />} />
            <Route path="notes/:clientId" element={<AdminClientCalendar />} />
            <Route path="appointments" element={<AdminAppointments />} />
            <Route path="notifications" element={<AdminNotifications />} />
            <Route path="accounts" element={<AdminAccounts />} />
            <Route path="alerts" element={<AdminBudgetTracking />} />
            <Route path="budget" element={<AdminBudgetTracking />} />
            <Route path="audit" element={<AdminAuditReport />} />
            <Route path="forms" element={<AdminFormBuilder />} />
            <Route path="resources" element={<AdminResources />} />
            <Route path="profiles" element={<AdminProfiles />} />
            <Route path="profiles/client/:clientId" element={<AdminClientProfile />} />
            <Route path="profiles/contractor/:contractorId" element={<AdminContractorProfile />} />
            <Route path="links" element={<AdminLinks />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="change-requests" element={<AdminChangeRequests />} />
            <Route path="med-requests" element={<AdminMedRequests />} />
            <Route path="provider-proxy" element={<DailyNoteForm />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-center" />
    </AuthProvider>
  );
}
